import { intlLocaleFor } from "../i18n/locales";
import { useState, useEffect } from "react";
import {
  Loader2, Mail, Phone, ShieldCheck, ShieldOff, Link2, Link2Off,
  Ban, RotateCcw, Check, Building2, UserRound, Shield, FileText, Users,
} from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import { api, apiError } from "../lib/api";
import i18n from "../i18n";

// Traduction (Mon espace) : les textes suivent la langue active ; la page parente s'abonne aux changements via useTranslation.
const tr = (k, o) => i18n.t(k, o);

const REL_LABELS = { internal: "Interne", external: "Externe", other: "Autre" };

const Row = ({ icon, label, children }) => (
  <div className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0">
    <div className="mt-0.5 text-slate-400">{icon}</div>
    <div className="min-w-0 flex-1">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="text-sm text-slate-800 break-words">{children}</div>
    </div>
  </div>
);

export const DirectoryContactDialog = ({ open, onOpenChange, contactId, canManage, onChanged }) => {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/directory/contacts/${contactId}`);
      setContact(data);
      setForm({
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        display_name: data.display_name || "",
        organization_name: data.organization_name || "",
        relationship_type: data.relationship_type || "external",
        phone: data.phone || "",
        email: data.email || "",
      });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || tr("directory:contact.contactIntrouvable"));
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && contactId) { setEditing(false); load(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contactId]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Action impossible.");
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = () =>
    act(async () => {
      await api.patch(`/directory/contacts/${contactId}`, form);
      setEditing(false);
    }, tr("directory:contact.contactMisAJour"));

  if (!contact && !loading) return null;

  const deviceLabel = { none: tr("directory:contact.aucun"), active: tr("directory:contact.actif"), revoked: tr("directory:contact.revoque") };
  const srcLabel = { manual: tr("directory:contact.manual"), workspace_member: tr("directory:contact.membre"), csv: "CSV", active_directory: "AD", entra_id: "Entra ID", scim: "SCIM" };
  const fmtDate = (s) => { if (!s) return ""; try { return new Date(s).toLocaleDateString(intlLocaleFor(i18n.language), { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return ""; } };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="directory-contact-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserRound className="w-5 h-5 text-emerald-600" />
            {loading ? "Chargement…" : (contact?.display_name || contact?.email)}
          </DialogTitle>
          <DialogDescription>{tr("directory:contact.ficheContactDeL")}</DialogDescription>
        </DialogHeader>

        {loading || !contact ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : editing ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-600 mb-1 block text-xs">{tr("directory:contact.prenom")}</Label>
                <Input data-testid="edit-first-name" value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} className="bg-slate-50 border-slate-200" /></div>
              <div><Label className="text-slate-600 mb-1 block text-xs">{tr("directory:contact.nom")}</Label>
                <Input data-testid="edit-last-name" value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} className="bg-slate-50 border-slate-200" /></div>
            </div>
            <div><Label className="text-slate-600 mb-1 block text-xs">{tr("directory:contact.nomAffiche")}</Label>
              <Input data-testid="edit-display-name" value={form.display_name} onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} className="bg-slate-50 border-slate-200" /></div>
            <div><Label className="text-slate-600 mb-1 block text-xs">{tr("directory:contact.societe")}</Label>
              <Input data-testid="edit-organization" value={form.organization_name} onChange={(e) => setForm((f) => ({ ...f, organization_name: e.target.value }))} className="bg-slate-50 border-slate-200" /></div>
            <div><Label className="text-slate-600 mb-1 block text-xs">{tr("directory:contact.eMail")}</Label>
              <Input data-testid="edit-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="bg-slate-50 border-slate-200" /></div>
            <div><Label className="text-slate-600 mb-1 block text-xs">{tr("directory:contact.telephone")}</Label>
              <Input data-testid="edit-phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+687 ..." className="bg-slate-50 border-slate-200" /></div>
            <div><Label className="text-slate-600 mb-1 block text-xs">{tr("directory:contact.typeDeRelation")}</Label>
              <Select value={form.relationship_type} onValueChange={(v) => setForm((f) => ({ ...f, relationship_type: v }))}>
                <SelectTrigger data-testid="edit-relationship" className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">{tr("directory:contact.interne")}</SelectItem>
                  <SelectItem value="external">{tr("directory:contact.externe")}</SelectItem>
                  <SelectItem value="other">{tr("directory:contact.autre")}</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="flex gap-2 pt-2">
              <Button data-testid="save-contact-edit-btn" onClick={saveEdit} disabled={busy} className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}{tr("directory:contact.enregistrer")}
              </Button>
              <Button variant="outline" onClick={() => setEditing(false)} className="border-slate-200">{tr("directory:contact.annuler")}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            <Row icon={<Mail className="w-4 h-4" />} label={tr("directory:contact.eMail")}>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span>{contact.email}</span>
                  {contact.email_verified
                    ? <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30"><ShieldCheck className="w-3 h-3 mr-1" />{tr("directory:contact.valide")}</Badge>
                    : <Badge variant="outline" className="text-slate-500 border-slate-300">{tr("directory:contact.nonValide")}</Badge>}
                </div>
                {contact.email_verified && (contact.email_verified_at || contact.email_verified_by) && (
                  <div className="text-xs text-slate-400 mt-0.5">
                    {tr("directory:contact.valide")} {fmtDate(contact.email_verified_at)}{contact.email_verified_by ? tr("directory:contact.verifiedBy", { who: contact.email_verified_by }) : ""}
                  </div>
                )}
              </div>
            </Row>
            <Row icon={<Phone className="w-4 h-4" />} label={tr("directory:contact.telephone")}>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span>{contact.phone || "—"}</span>
                  {contact.phone && (contact.phone_verified
                    ? <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30"><ShieldCheck className="w-3 h-3 mr-1" />{tr("directory:contact.valide")}</Badge>
                    : <Badge variant="outline" className="text-slate-500 border-slate-300">{tr("directory:contact.nonValide")}</Badge>)}
                </div>
                {contact.phone && contact.phone_verified && (contact.phone_verified_at || contact.phone_verified_by) && (
                  <div className="text-xs text-slate-400 mt-0.5">
                    {tr("directory:contact.valide")} {fmtDate(contact.phone_verified_at)}{contact.phone_verified_by ? tr("directory:contact.verifiedBy", { who: contact.phone_verified_by }) : ""}
                  </div>
                )}
              </div>
            </Row>
            <Row icon={<Building2 className="w-4 h-4" />} label={tr("directory:contact.societe")}>{contact.organization_name || "—"}</Row>
            <Row icon={<UserRound className="w-4 h-4" />} label={tr("directory:contact.type")}>{REL_LABELS[contact.relationship_type] || contact.relationship_type}</Row>
            <Row icon={<FileText className="w-4 h-4" />} label={tr("directory:contact.source")}>{srcLabel[contact.source] || contact.source || tr("directory:contact.manual")}</Row>
            <Row icon={<Link2 className="w-4 h-4" />} label={tr("directory:contact.lienSecurepass")}>
              {contact.linked
                ? <Badge className="bg-cyan-500/15 text-cyan-700 border-cyan-500/30">{tr("directory:contact.compteLie")}</Badge>
                : (contact.link_state === "unlinked_by_user"
                    ? <span className="text-slate-500">{tr("directory:contact.desolidariseParLUtilisateur")}</span>
                    : <span className="text-slate-500">{tr("directory:contact.nonLie")}</span>)}
            </Row>
            <Row icon={<Users className="w-4 h-4" />} label={tr("directory:contact.membreDeCeWorkspace")}>
              {contact.is_member
                ? <span className="inline-flex items-center gap-2"><Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30">{tr("directory:contact.membre")}</Badge>{contact.member_role_label && <span className="text-xs text-slate-500">{tr(`common:roles.${contact.member_role}`, { defaultValue: contact.member_role_label })}</span>}</span>
                : <span className="text-slate-500">{tr("directory:contact.nonMembre")}</span>}
            </Row>
            <Row icon={<Shield className="w-4 h-4" />} label={tr("directory:contact.appareilSecurise")}>
              <span className="text-slate-500">{deviceLabel[contact.secure_device_status] || tr("directory:contact.aucun")}</span>
            </Row>
            <Row icon={<Check className="w-4 h-4" />} label={tr("directory:contact.statut")}>
              {contact.status === "active"
                ? <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30">{tr("directory:contact.actif")}</Badge>
                : <Badge variant="outline" className="text-amber-600 border-amber-300">{tr("directory:contact.desactive")}</Badge>}
            </Row>

            {canManage && (
              <div className="pt-4 space-y-2">
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" data-testid="contact-edit-btn" onClick={() => setEditing(true)} className="border-slate-200">{tr("directory:contact.modifier")}</Button>
                  {contact.email_verified
                    ? <Button size="sm" variant="outline" data-testid="unverify-email-btn" disabled={busy} onClick={() => act(() => api.post(`/directory/contacts/${contactId}/unverify-email`), tr("directory:contact.eMailDevalide"))} className="border-slate-200"><ShieldOff className="w-3.5 h-3.5 mr-1" />{tr("directory:contact.devaliderEMail")}</Button>
                    : <Button size="sm" variant="outline" data-testid="verify-email-btn" disabled={busy} onClick={() => act(() => api.post(`/directory/contacts/${contactId}/verify-email`), tr("directory:contact.eMailValide"))} className="border-slate-200"><ShieldCheck className="w-3.5 h-3.5 mr-1" />{tr("directory:contact.validerEMail")}</Button>}
                  {contact.phone && (contact.phone_verified
                    ? <Button size="sm" variant="outline" data-testid="unverify-phone-btn" disabled={busy} onClick={() => act(() => api.post(`/directory/contacts/${contactId}/unverify-phone`), tr("directory:contact.telephoneDevalide"))} className="border-slate-200"><ShieldOff className="w-3.5 h-3.5 mr-1" />{tr("directory:contact.devaliderTel")}</Button>
                    : <Button size="sm" variant="outline" data-testid="verify-phone-btn" disabled={busy} onClick={() => act(() => api.post(`/directory/contacts/${contactId}/verify-phone`), tr("directory:contact.telephoneValide"))} className="border-slate-200"><ShieldCheck className="w-3.5 h-3.5 mr-1" />{tr("directory:contact.validerTel")}</Button>)}
                </div>
                <div>
                  {contact.status === "active"
                    ? <Button size="sm" variant="outline" data-testid="disable-contact-btn" disabled={busy} onClick={() => act(() => api.post(`/directory/contacts/${contactId}/disable`), tr("directory:contact.contactDesactive"))} className="border-red-200 text-red-600 hover:bg-red-50"><Ban className="w-3.5 h-3.5 mr-1" />{tr("directory:contact.desactiver")}</Button>
                    : <Button size="sm" variant="outline" data-testid="enable-contact-btn" disabled={busy} onClick={() => act(() => api.post(`/directory/contacts/${contactId}/enable`), tr("directory:contact.contactReactive"))} className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"><RotateCcw className="w-3.5 h-3.5 mr-1" />{tr("directory:contact.reactiver")}</Button>}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DirectoryContactDialog;
